import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";

const RULE_MAX_LEN = 200;
const WORDS_MAX_LEN = 15;
const DEFAULT_WORD_FONT_NUM = 2;
const MIN_WORD_FONT_REM = 1;
const MAX_WORD_FONT_REM = 20;
const SUBMISSION_MODE_IMAGE = "image";
const SUBMISSION_MODE_WORD = "word";

function normalizeSubmissionMode(raw) {
  return String(raw || "").trim().toLowerCase() === SUBMISSION_MODE_WORD
    ? SUBMISSION_MODE_WORD
    : SUBMISSION_MODE_IMAGE;
}

/** Parse "5", "5rem", "12.5 rem" → number, or null. */
function parseFontNumber(raw) {
  const m = String(raw ?? "")
    .trim()
    .match(/^(\d+(?:\.\d+)?)\s*(rem)?$/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  return Number.isFinite(n) ? n : null;
}

/** Clamp to 1–20 and return rem string for API / frontend (e.g. "5rem"). */
function toWordFontRem(raw, { warn = false } = {}) {
  let n = parseFontNumber(raw);
  if (n == null) n = DEFAULT_WORD_FONT_NUM;
  if (n > MAX_WORD_FONT_REM) {
    if (warn) {
      Swal.fire(
        "Limit Exceeded",
        `Font size can be at most ${MAX_WORD_FONT_REM}.`,
        "warning"
      );
    }
    n = MAX_WORD_FONT_REM;
  }
  if (n < MIN_WORD_FONT_REM) n = MIN_WORD_FONT_REM;
  // Prefer whole numbers when possible (5 → "5rem").
  const out = Number.isInteger(n) ? String(n) : String(n);
  return `${out}rem`;
}

/** Number string for the admin input (e.g. "5"). */
function toFontInputValue(raw) {
  const n = parseFontNumber(raw);
  if (n == null) return String(DEFAULT_WORD_FONT_NUM);
  const clamped = Math.min(MAX_WORD_FONT_REM, Math.max(MIN_WORD_FONT_REM, n));
  return Number.isInteger(clamped) ? String(clamped) : String(clamped);
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [rules, setRules] = useState([""]);
  const [words, setWords] = useState("");
  const [wordFontSize, setWordFontSize] = useState(String(DEFAULT_WORD_FONT_NUM));
  const [submissionMode, setSubmissionMode] = useState(SUBMISSION_MODE_IMAGE);

  const [colors, setColors] = useState({
    text_color: "#000000",
    button_color: "#ffffff",
    button_backgroundcolor: "#000000",
  });

  // fetch theme on mount / theme change
  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setRules(data.rules || [""]);

    if ("words" in data) {
      setWords(String(data.words ?? "").slice(0, WORDS_MAX_LEN));
    }
    if ("word_font_size" in data) {
      setWordFontSize(toFontInputValue(data.word_font_size));
    }
    if ("submission_mode" in data) {
      setSubmissionMode(normalizeSubmissionMode(data.submission_mode));
    }

    const c = data?.colors;
    const flat = data && !c && (data.text_color != null || data.button_color != null);
    const source = c || (flat ? data : null);
    setColors((prev) => ({
      text_color: (source?.text_color ?? data.text_color) ?? prev.text_color,
      button_color: (source?.button_color ?? data.button_color) ?? prev.button_color,
      button_backgroundcolor:
        (source?.button_backgroundcolor ?? data.button_backgroundcolor) ?? prev.button_backgroundcolor,
    }));
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // --- rules handlers ---
  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > RULE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Rules can be at most ${RULE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      newVal = val.slice(0, RULE_MAX_LEN);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 7) {
      return Swal.fire("Limit Reached", "You can only add up to 7 rules.", "warning");
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one rule.",
        "warning"
      );
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleWordsChange = (val) => {
    setWords(String(val ?? "").slice(0, WORDS_MAX_LEN));
  };

  const handleWordFontSizeChange = (val) => {
    const next = String(val ?? "");
    // Digits only (optional single decimal while typing).
    if (next !== "" && !/^\d*\.?\d*$/.test(next)) return;

    if (next === "" || next === ".") {
      setWordFontSize(next);
      return;
    }

    const n = parseFloat(next);
    if (!Number.isNaN(n) && n > MAX_WORD_FONT_REM) {
      Swal.fire(
        "Limit Exceeded",
        `Font size can be at most ${MAX_WORD_FONT_REM}.`,
        "warning"
      );
      setWordFontSize(String(MAX_WORD_FONT_REM));
      return;
    }
    setWordFontSize(next);
  };

  // --- submit ---
  const handleSubmit = async () => {
    const payload = {
      data: {
        submission_mode: normalizeSubmissionMode(submissionMode),
        words: String(words ?? "").slice(0, WORDS_MAX_LEN),
        word_font_size: toWordFontRem(wordFontSize, { warn: true }),
        text_color: colors.text_color,
        button_color: colors.button_color,
        button_backgroundcolor: colors.button_backgroundcolor,
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", "Your changes have been saved.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save. Please try again.", "error");
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game Settings
                </div>

                <div className="rules-field-row">
                  <label className="rules-field-label">Text Color</label>
                  <div className="rules-field-control">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.text_color}
                      onChange={handleColorChange("text_color")}
                    />
                    <span className="color-code">{colors.text_color}</span>
                  </div>
                </div>

                <div className="rules-field-row">
                  <label className="rules-field-label">Button Text Color</label>
                  <div className="rules-field-control">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.button_color}
                      onChange={handleColorChange("button_color")}
                    />
                    <span className="color-code">{colors.button_color}</span>
                  </div>
                </div>

                <div className="rules-field-row">
                  <label className="rules-field-label">Button Background Color</label>
                  <div className="rules-field-control">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.button_backgroundcolor}
                      onChange={handleColorChange("button_backgroundcolor")}
                    />
                    <span className="color-code">{colors.button_backgroundcolor}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">Player contribution</div>

                <div className="form-group-v1">
                  <label>Mode</label>
                  <div className="submission-mode-toggle" role="group" aria-label="Player contribution mode">
                    <button
                      type="button"
                      className={
                        submissionMode === SUBMISSION_MODE_IMAGE
                          ? "submission-mode-btn is-active"
                          : "submission-mode-btn"
                      }
                      onClick={() => setSubmissionMode(SUBMISSION_MODE_IMAGE)}
                    >
                      Image upload
                    </button>
                    <button
                      type="button"
                      className={
                        submissionMode === SUBMISSION_MODE_WORD
                          ? "submission-mode-btn is-active"
                          : "submission-mode-btn"
                      }
                      onClick={() => setSubmissionMode(SUBMISSION_MODE_WORD)}
                    >
                      Word text
                    </button>
                  </div>
                  <div className="code">
                    * Only one mode is active. Players either upload an image or enter a word
                    (max {WORDS_MAX_LEN} characters).
                  </div>
                </div>

                <div className="form-group-v1">
                  <label>Mosaic Word</label>
                  <input
                    className="rule-input"
                    type="text"
                    value={words}
                    onChange={(e) => handleWordsChange(e.target.value)}
                    placeholder="e.g. India"
                    maxLength={WORDS_MAX_LEN}
                  />
                  <div className="code">
                    {submissionMode === SUBMISSION_MODE_WORD
                      ? `* Outline shape for the mosaic (border only). Player words fill inside. Max ${WORDS_MAX_LEN} chars.`
                      : `* Shown as the mosaic word filled with photos. Maximum ${WORDS_MAX_LEN} characters.`}
                  </div>
                </div>

                <div className="form-group-v1">
                  <label>Word Font Size</label>
                  <input
                    className="rule-input"
                    type="number"
                    inputMode="numeric"
                    min={MIN_WORD_FONT_REM}
                    max={MAX_WORD_FONT_REM}
                    step={1}
                    value={wordFontSize}
                    onChange={(e) => handleWordFontSizeChange(e.target.value)}
                    placeholder="e.g. 5"
                  />
                  <div className="code">
                    * Enter a number {MIN_WORD_FONT_REM}–{MAX_WORD_FONT_REM}{" "}
                    (saved as rem, e.g. 5 → 5rem). Examples: 5, 6, 8, 12
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save 
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
